/**
 * @fileoverview In-app browser (WebView) detection for analytics and UX hints.
 * @description Identifies embedded browsers inside social apps where referrers are
 * stripped and ITP is stricter — a fast-growing segment per GA (+275% Safari in-app).
 *
 * @module lib/analytics/in-app-browser
 */

/** Known in-app browser hosts keyed for low-cardinality analytics. */
export type InAppBrowserApp =
  | "instagram"
  | "facebook"
  | "linkedin"
  | "tiktok"
  | "twitter"
  | "snapchat"
  | "pinterest"
  | "line"
  | "wechat"
  | "unknown"

/**
 * Result of in-app browser detection.
 *
 * @typedef {Object} InAppBrowserInfo
 * @property {boolean} isInApp - Whether the UA looks like an embedded WebView.
 * @property {InAppBrowserApp | null} app - Detected host app, if known.
 * @property {boolean} isIOS - Whether the UA indicates iOS / iPadOS.
 */
export interface InAppBrowserInfo {
  readonly isInApp: boolean
  readonly app: InAppBrowserApp | null
  readonly isIOS: boolean
}

/** UA substring needles → analytics app label. Order matters (first match wins). */
const IN_APP_UA_PATTERNS: ReadonlyArray<readonly [RegExp, InAppBrowserApp]> = [
  [/Instagram/i, "instagram"],
  [/FBAN|FBAV|FB_IAB/i, "facebook"],
  [/LinkedInApp/i, "linkedin"],
  [/musical_ly|BytedanceWebview|trill_/i, "tiktok"],
  [/Twitter/i, "twitter"],
  [/Snapchat/i, "snapchat"],
  [/Pinterest/i, "pinterest"],
  [/Line\//i, "line"],
  [/MicroMessenger/i, "wechat"],
]

/**
 * Detects whether the current browser is an in-app WebView and which app hosts it.
 *
 * @returns {InAppBrowserInfo} Detection result; safe on the server (all false/null).
 */
export function detectInAppBrowser(): InAppBrowserInfo {
  if (typeof navigator === "undefined") {
    return { isInApp: false, app: null, isIOS: false }
  }

  const ua = navigator.userAgent
  const isIOS = /iPhone|iPad|iPod/i.test(ua)

  for (const [pattern, app] of IN_APP_UA_PATTERNS) {
    if (pattern.test(ua)) {
      return { isInApp: true, app, isIOS }
    }
  }

  // Generic iOS WebView: AppleWebKit without a standalone Safari token.
  const isGenericIosWebView =
    isIOS && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua)
  if (isGenericIosWebView) {
    return { isInApp: true, app: "unknown", isIOS: true }
  }

  return { isInApp: false, app: null, isIOS }
}

/**
 * Human-readable label for checkout/auth in-app hints.
 *
 * @param {InAppBrowserApp | null} app - Detected in-app host.
 * @returns {string} Display name for the detected app.
 */
export function inAppBrowserDisplayName(app: InAppBrowserApp | null): string {
  switch (app) {
    case "instagram":
      return "Instagram"
    case "facebook":
      return "Facebook"
    case "linkedin":
      return "LinkedIn"
    case "tiktok":
      return "TikTok"
    case "twitter":
      return "X (Twitter)"
    case "snapchat":
      return "Snapchat"
    case "pinterest":
      return "Pinterest"
    case "line":
      return "LINE"
    case "wechat":
      return "WeChat"
    case "unknown":
      return "this app"
    case null:
      return "your browser"
    default: {
      const exhaustive: never = app
      return exhaustive
    }
  }
}
